import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { IamPolicyService } from '../components/iam/services/iam-policy.service';
import { FeatureFlagService } from '../components/my-profile/services/feature-flag.service';
import { ServiceRegistryService } from '../components/services/service-registry/services/service-registry.service';
import { PreferencesService } from './preferences.service';
import { isModernShell } from '../components/shell/common';

const PINNED_PREFS_KEY = 'dashboardPreferences';
const RECENTLY_USED_KEY = 'recentlyUsedServices';
const MAX_RECENT = isModernShell() ? 4 : 10;
const DEFAULT_ALLOWED_SERVICES = ['dashboard', 'customer-db', 't-rex', 'simple-vpn', 'monitoring-hub'];

export interface ServiceType {
	name: string;
	displayName: string;
	route: string;
	category?: string;
	description?: string;
	keywords?: string[];
	relatedservices?: string[];
	isGeneric?: boolean;
	external?: string;
}

@Injectable({
	providedIn: 'root',
})
export class ServiceNavigationService {
	private servicesSubject = new BehaviorSubject<ServiceType[]>([]);
	private allowedServicesSubject = new BehaviorSubject<string[]>(DEFAULT_ALLOWED_SERVICES);
	private loadedSubject = new BehaviorSubject<boolean>(false);
	private pinnedSubject: BehaviorSubject<string[]>;
	private recentlyUsedSubject: BehaviorSubject<string[]>;

	public services$: Observable<ServiceType[]> = this.servicesSubject.asObservable();
	public allowedServices$: Observable<string[]> = this.allowedServicesSubject.asObservable();
	public loaded$: Observable<boolean> = this.loadedSubject.asObservable();
	public pinned$: Observable<string[]>;
	public recentlyUsed$: Observable<ServiceType[]>;

	constructor(
		private readonly serviceRegistryService: ServiceRegistryService,
		private readonly policyService: IamPolicyService,
		private readonly featureFlagService: FeatureFlagService,
		private readonly preferencesService: PreferencesService,
		private readonly router: Router
	) {
		const stored = preferencesService.getPreference<{ pinnedServices: string[] }>(PINNED_PREFS_KEY)?.pinnedServices ?? [];
		this.pinnedSubject = new BehaviorSubject<string[]>(stored);
		this.pinned$ = this.pinnedSubject.asObservable();

		this.recentlyUsedSubject = new BehaviorSubject<string[]>(this.loadRecentlyUsedNames());
		this.recentlyUsed$ = combineLatest([this.recentlyUsedSubject, this.servicesSubject]).pipe(
			map(([names, services]) =>
				names
					.slice(-MAX_RECENT)
					.reverse()
					.map((name) => services.find((s) => s.name === name))
					.filter((s): s is ServiceType => !!s)
			)
		);
	}

	private loadRecentlyUsedNames(): string[] {
		try {
			const raw = localStorage.getItem(RECENTLY_USED_KEY);
			if (!raw) return [];
			const stored = JSON.parse(raw);
			if (!stored.length) return [];
			if (typeof stored[0] === 'string') return stored;
			return stored.map((s: any) => s.name ?? s.serviceName).filter(Boolean);
		} catch {
			return [];
		}
	}

	public togglePin(serviceName: string): void {
		const current = [...this.pinnedSubject.getValue()];
		const idx = current.indexOf(serviceName);
		if (idx >= 0) {
			current.splice(idx, 1);
		} else {
			current.push(serviceName);
		}
		this.pinnedSubject.next(current);
		this.preferencesService.setPreference({ [PINNED_PREFS_KEY]: { pinnedServices: current } });
	}

	public isPinned(serviceName: string): boolean {
		return this.pinnedSubject.getValue().includes(serviceName);
	}

	public get pinnedNames(): string[] {
		return this.pinnedSubject.getValue();
	}

	public async loadServicePolicies(principal?: string): Promise<void> {
		const defaultServices: ServiceType[] = [{ name: 'dashboard', displayName: 'Dashboard', route: 'dashboard' }];

		if (this.featureFlagService.getFeatureFlagState('pricingCalculator')) {
			defaultServices.push({
				name: 'Pricing Calculator',
				displayName: 'Pricing Calculator',
				route: 'pricing-calculator',
			});
		}

		try {
			const allAvailableServices = await this.serviceRegistryService.getAvailableServices();
			const serviceArray: ServiceType[] = [];

			const notificationsEnabled = this.featureFlagService.getFeatureFlagState('notifications');

			allAvailableServices
				.filter((service: any) => service.route)
				.filter((service: any) => service.route !== 'notifications' || notificationsEnabled)
				.forEach((service) =>
					serviceArray.push({
						name: service.serviceName,
						displayName: service.route === 'coming-soon' ? service.displayName + ' (coming soon)' : service.displayName,
						route: service.route,
						category: service.category,
						description: service.shortDescription,
						keywords: service.keywords,
					})
				);

			serviceArray.sort((a, b) => a.displayName.localeCompare(b.displayName));

			this.servicesSubject.next([...defaultServices, ...serviceArray]);
			this.loadedSubject.next(true);

			if (principal) {
				await this.updateAllowedServices(principal);
			}
		} catch (e) {
			console.error('Error loading service policies:', e);
		}
	}

	public async updateAllowedServices(principal: string): Promise<void> {
		const defaultAllowedServices = DEFAULT_ALLOWED_SERVICES;
		const principalServices = await this.policyService.getActualPrincipalServices(principal);
		this.allowedServicesSubject.next([...defaultAllowedServices, ...principalServices]);
	}

	public getServiceUrl(service: ServiceType): string | null {
		if (service.route === 'coming-soon') return null;
		if (service.external) return service.external;
		if (service.isGeneric) return `/gen/${service.route}`;
		return `/${service.route}`;
	}

	public getRouterCommands(service: ServiceType): string[] | null {
		if (service.route === 'coming-soon' || service.external) return null;
		if (service.isGeneric) return ['/', 'gen', service.route];
		return ['/', service.route];
	}

	public trackRecentlyUsed(service: ServiceType): void {
		this.saveToRecentlyUsed(service);
	}

	public openService(service: ServiceType): void {
		if (service.route === 'coming-soon') return;

		if (service.isGeneric) {
			this.router.navigate(['gen', service.route]);
		} else if (service.external) {
			window.open(service.external, '_blank');
		} else {
			this.router.navigate([service.route]);
			this.saveToRecentlyUsed(service);
		}
	}

	private saveToRecentlyUsed(service: ServiceType): void {
		if (service.name === 'dashboard') return;
		const names = [...this.recentlyUsedSubject.getValue()];
		const idx = names.indexOf(service.name);
		if (idx !== -1) names.splice(idx, 1);
		names.push(service.name);
		this.recentlyUsedSubject.next(names);
		localStorage.setItem(RECENTLY_USED_KEY, JSON.stringify(names));
	}

	public filterServices(searchTerm: string): ServiceType[] {
		if (!searchTerm) return this.servicesSubject.value;
		const term = searchTerm.toLowerCase();

		return this.servicesSubject.value.filter(
			(s) =>
				s.displayName.toLowerCase().includes(term) ||
				s.name.toLowerCase().includes(term) ||
				s.keywords?.some((k) => k.toLowerCase().includes(term))
		);
	}

	public get services(): ServiceType[] {
		return this.servicesSubject.value;
	}

	public get allowedServices(): string[] {
		return this.allowedServicesSubject.value;
	}
}
